import { useEffect, useState, type FormEvent } from "react";

import { Alert, Badge, Button, Chip, ChipGroup, Input, Tab, TabList } from "@realroof/ui";

import { api, LEVEL_LABEL, myLevels, STATUS_LABEL, type Level, type QueueItem, type Status } from "../api";
import { useSession } from "../session";
import { rupees, waiting, when } from "../format";
import { Link, navigate, useLocation } from "../router";
import { Table, td, th } from "../table";

type TabKey = Status | "changes";
const TABS: [TabKey, string][] = [
  ["under_review", STATUS_LABEL.under_review],
  ["changes", "Changes to review"],
  ["live", STATUS_LABEL.live],
  ["rejected", STATUS_LABEL.rejected],
  ["deactivated", STATUS_LABEL.deactivated],
  ["deleted", STATUS_LABEL.deleted],
];
const COUNT_KEY: Partial<Record<TabKey, string>> = { changes: "changes_pending" };
const MODULES = [
  ["", "All"],
  ["property", "Property"],
  ["hall", "Function halls"],
  ["service", "Services"],
] as const;

/** Ads by status; "Waiting review" is the moderation queue, oldest first. */
export function Queue() {
  const { query } = useLocation();
  const status = (query.get("status") as TabKey) || "under_review";
  const { me } = useSession();
  const module = query.get("module") ?? "";
  // Staff limited to some kinds of ads see only those chips ("All" when more than one)
  const kinds = MODULES.filter(([m]) => (m ? !me?.ad_modules || me.ad_modules.includes(m) : !me?.ad_modules || me.ad_modules.length > 1));
  const q = query.get("q") ?? "";
  // Two-level review: what waits for level 1 or level 2 (a checker starts on their own level)
  const reviewing = status === "under_review" || status === "changes";
  const own = myLevels(me);
  const picked = query.get("stage");
  const stage = (picked === "all" ? "" : (picked ?? (own.length === 1 ? own[0] : ""))) as Level | "";
  const [items, setItems] = useState<QueueItem[] | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [search, setSearch] = useState(q);
  const [error, setError] = useState("");

  useEffect(() => {
    setItems(null);
    const params = new URLSearchParams({ status });
    if (module) params.set("module", module);
    if (q) params.set("q", q);
    if (reviewing && stage) params.set("stage", stage);
    api<QueueItem[]>(`/admin/listings?${params}`)
      .then(setItems)
      .catch((err: Error) => setError(err.message));
    api<Record<string, number>>("/admin/stats")
      .then(setCounts)
      .catch(() => undefined);
  }, [status, module, q, stage, reviewing]);

  function go(patch: Record<string, string>) {
    const params = new URLSearchParams({ status, module, q, stage: query.get("stage") ?? "", ...patch });
    for (const [k, v] of [...params]) if (!v) params.delete(k);
    navigate(`/ads?${params}`);
  }

  function onSearch(e: FormEvent) {
    e.preventDefault();
    go({ q: search.trim() });
  }

  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Ads</h1>
      <TabList>
        {TABS.map(([st, label]) => {
          const n = counts[COUNT_KEY[st] ?? st];
          return (
            <Tab key={st} selected={st === status} className="flex-none" onClick={() => go({ status: st })}>
              {label}
              {n ? <Badge className="ml-1.5">{n}</Badge> : null}
            </Tab>
          );
        })}
      </TabList>
      <div className="flex flex-wrap items-center gap-3">
        <ChipGroup role="group" aria-label="Kind of ad">
          {kinds.map(([m, label]) => (
            <Chip key={m} aria-pressed={m === module || kinds.length === 1} onClick={() => go({ module: m })}>
              {label}
            </Chip>
          ))}
        </ChipGroup>
        {reviewing && (
          <ChipGroup role="group" aria-label="Review level">
            {(["", "l1", "l2"] as const).map((l) => (
              <Chip key={l || "all"} aria-pressed={stage === l} onClick={() => go({ stage: l || "all" })}>
                {l ? `Waiting ${LEVEL_LABEL[l].toLowerCase()}` : "Both levels"}
                {l && counts[`waiting_${l}`] ? <Badge className="ml-1.5">{counts[`waiting_${l}`]}</Badge> : null}
              </Chip>
            ))}
          </ChipGroup>
        )}
        <form onSubmit={onSearch} className="flex flex-1 flex-wrap gap-2 md:ml-auto md:max-w-md">
          <Input className="flex-1" aria-label="Search ads" placeholder="Ad number, title, owner mobile or email" value={search} onChange={(e) => setSearch(e.target.value)} />
          <Button type="submit">Search</Button>
        </form>
      </div>
      {error && <Alert tone="error">{error}</Alert>}
      {items === null && !error && <p className="text-muted-foreground">Loading…</p>}
      {items?.length === 0 && <p className="text-muted-foreground">{status === "under_review" ? "Nothing waiting. Well done." : "No ads here."}</p>}
      {items && items.length > 0 && (
        <Table>
          <thead>
            <tr>
              <th className={th}>Ad</th>
              <th className={th}>Place</th>
              <th className={th}>Price</th>
              <th className={th}>Owner</th>
              <th className={th}>Photos</th>
              <th className={th}>{status === "under_review" ? "Waiting" : "Updated"}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="hover:bg-muted/60">
                <td className={td}>
                  <Link to={`/ads/${a.id}`} className="font-semibold">
                    {a.title}
                  </Link>
                  <div className="font-mono text-xs text-muted-foreground">
                    {a.public_id} · {a.module}
                    {a.plan === "premium" ? " · Premium" : ""}
                  </div>
                  {reviewing && (
                    <div className="text-xs">
                      {a.check_l1_at ? `Level 1 checked by ${a.check_l1_name ?? "staff"}` : <span className="text-muted-foreground">Needs level 1</span>}
                    </div>
                  )}
                </td>
                <td className={td}>{[a.locality, a.city].filter(Boolean).join(", ") || "—"}</td>
                <td className={`${td} whitespace-nowrap`}>{rupees(a.price)}</td>
                <td className={td}>
                  {a.owner.full_name || "—"}
                  <div className="text-xs text-muted-foreground">{a.owner.mobile}</div>
                </td>
                <td className={td}>{a.photos}</td>
                <td className={`${td} whitespace-nowrap`} title={when(a.submitted_at)}>
                  {status === "under_review" ? waiting(a.submitted_at) : when(a.reviewed_at ?? a.submitted_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </section>
  );
}
